import type { Product } from 'src/components/restructure/product/types/product'
import type { ProductItem } from 'src/components/restructure/product/types/productItem'

type BuildItemListJsonLdArgs = {
  products?: Product[] | null
  siteUrl?: string | null
  pageUrl?: string | null
  name?: string | null
  description?: string | null
  currentPage?: number | null
  itemsPerPage?: number | null
}

function cleanString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }

  const trimmedValue = value.trim()

  return trimmedValue.length > 0 ? trimmedValue : undefined
}

function normalizeOrigin(siteUrl?: string | null): string | undefined {
  const cleanedSiteUrl = cleanString(siteUrl)

  return cleanedSiteUrl?.replace(/\/+$/, '')
}

function normalizeAbsoluteUrl(
  url: string | undefined,
  siteUrl?: string | null
): string | undefined {
  if (!url) {
    return undefined
  }

  if (/^https?:\/\//i.test(url)) {
    return url
  }

  const origin = normalizeOrigin(siteUrl)

  if (!origin) {
    return undefined
  }

  return `${origin}/${url.replace(/^\/+/, '')}`
}

function getPrimarySku(product: Product): ProductItem | undefined {
  return product.items?.find((item) => item.isAvailable) ?? product.items?.[0]
}

function itemConditionSchema(conditions?: string): string {
  return conditions?.toLowerCase() === 'novo'
    ? 'https://schema.org/NewCondition'
    : 'https://schema.org/UsedCondition'
}

function getProductUrl(
  product: Product,
  item: ProductItem | undefined,
  siteUrl?: string | null
): string | undefined {
  const linkText = cleanString(item?.linkText) ?? cleanString(product.linkText)

  if (linkText) {
    return normalizeAbsoluteUrl(`/${linkText}/p`, siteUrl)
  }

  const link = cleanString(product.link)

  if (!link) {
    return undefined
  }

  return normalizeAbsoluteUrl(
    link.endsWith('/p') ? link : `/${link}/p`,
    siteUrl
  )
}

function getProductImage(product: Product, item: ProductItem | undefined) {
  return (
    cleanString(product.seo?.images?.[0]?.url) ??
    cleanString(item?.images?.[0]?.imageUrl)
  )
}

function buildOffer(
  item: ProductItem | undefined,
  productUrl: string,
  conditions?: string
): Record<string, unknown> | undefined {
  const sellerOffer = item?.sellerDefault?.sellerOffer
  const cashPrice = Number(sellerOffer?.cashPrice)
  const regularPrice = Number(sellerOffer?.price)
  const price =
    Number.isFinite(cashPrice) && cashPrice > 0 ? cashPrice : regularPrice

  if (!Number.isFinite(price) || price <= 0) {
    return undefined
  }

  const offer: Record<string, unknown> = {
    '@type': 'Offer',
    price: price.toFixed(2),
    priceCurrency: 'BRL',
    availability: sellerOffer?.isAvailable
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock',
    itemCondition: itemConditionSchema(conditions),
    url: productUrl,
  }

  if (sellerOffer?.priceValidUntil) {
    offer.priceValidUntil = sellerOffer.priceValidUntil
  }

  return offer
}

function buildProductJsonLd(
  product: Product,
  siteUrl?: string | null
): Record<string, unknown> | null {
  const primarySku = getPrimarySku(product)
  const productUrl = getProductUrl(product, primarySku, siteUrl)
  const productName = cleanString(product.productName)

  if (!productUrl || !productName) {
    return null
  }

  const productJsonLd: Record<string, unknown> = {
    '@type': 'Product',
    url: productUrl,
    name: productName,
  }

  const image = getProductImage(product, primarySku)
  const description =
    cleanString(product.seo?.description) ??
    cleanString(product.description) ??
    cleanString(primarySku?.description)

  const sku = cleanString(primarySku?.itemId) ?? cleanString(product.sku)
  const brand = cleanString(product.brand)
  const offer = buildOffer(primarySku, productUrl, product.conditions)

  if (image) {
    productJsonLd.image = image
  }

  if (description) {
    productJsonLd.description = description
  }

  if (sku) {
    productJsonLd.sku = sku
  }

  if (brand) {
    productJsonLd.brand = {
      '@type': 'Brand',
      name: brand,
    }
  }

  if (offer) {
    productJsonLd.offers = offer
  }

  return productJsonLd
}

function getStartPosition(
  currentPage?: number | null,
  itemsPerPage?: number | null
) {
  const normalizedPage = currentPage && currentPage > 0 ? currentPage : 1
  const normalizedItemsPerPage =
    itemsPerPage && itemsPerPage > 0 ? itemsPerPage : undefined

  if (!normalizedItemsPerPage) {
    return 1
  }

  return (normalizedPage - 1) * normalizedItemsPerPage + 1
}

export function buildItemListJsonLd({
  products,
  siteUrl,
  pageUrl,
  name,
  description,
  currentPage,
  itemsPerPage,
}: BuildItemListJsonLdArgs): Record<string, unknown> | null {
  if (!products?.length) {
    return null
  }

  const startPosition = getStartPosition(currentPage, itemsPerPage)
  const itemListElement = products
    .map((product, index) => {
      const item = buildProductJsonLd(product, siteUrl)

      if (!item) {
        return null
      }

      return {
        '@type': 'ListItem',
        position: startPosition + index,
        item,
      }
    })
    .filter(Boolean)

  if (!itemListElement.length) {
    return null
  }

  const itemList: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    numberOfItems: itemListElement.length,
    itemListElement,
  }

  const canonicalUrl = (() => {
    const absoluteUrl = normalizeAbsoluteUrl(cleanString(pageUrl), siteUrl)

    if (!absoluteUrl) {
      return undefined
    }

    try {
      const parsedUrl = new URL(absoluteUrl)

      parsedUrl.searchParams.delete('zipCode')

      return parsedUrl.toString()
    } catch {
      return absoluteUrl
    }
  })()

  const pageName = cleanString(name)
  const pageDescription = cleanString(description)

  if (canonicalUrl) {
    itemList.url = canonicalUrl
  }

  if (pageName) {
    itemList.name = pageName
  }

  if (pageDescription) {
    itemList.description = pageDescription
  }

  return itemList
}
